import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { Modal } from '../modal'
import { Input } from '../input'
import { Button } from '../button'
import { ErrorsList } from '../errors'
import { SelectWrapper } from '../select-wrapper'
import { CustomSelect } from '../custom-select'
import { useToast } from '../toast'
import { memberApi } from 'src/services/members/api'
import { memberQueries, Role } from 'src/services/members'
import {
  InviteMemberForm,
  InviteMemberFormInputs,
  TInviteMemberForm,
} from 'src/services/members/forms'
import { useAuth } from 'src/stores/auth'
import { SelectOption } from 'src/services/base-model'

/**
 * Role rank mapping mirroring the backend ROLE_RANK in server/rapa/core/views.py.
 * Roles not listed default to rank 0.
 */
const ROLE_RANK: Record<string, number> = {
  owner: 4,
  admin: 3,
  member: 2,
  viewer: 1,
}

function getRoleRank(roleName: string): number {
  return ROLE_RANK[roleName] ?? 0
}

interface InviteMemberModalProps {
  isOpen: boolean
  onClose: () => void
  orgId: string
}

const InviteMemberModalContent: React.FC<InviteMemberModalProps> = ({ isOpen, onClose, orgId }) => {
  const { form, createFormFieldChangeHandler } = useTnForm<TInviteMemberForm>()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useTranslation()
  const user = useAuth.use.user()
  const activeOrgId = useAuth.use.activeOrgId()

  const [apiError, setApiError] = useState<string | null>(null)

  const { data: roles } = useQuery(memberQueries.roles())

  // Determine the current user's role rank in the active org
  const currentUserRank = useMemo(() => {
    if (!user?.memberships || !activeOrgId) return 0
    const membership = user.memberships.find((m) => m.organizationId === activeOrgId)
    if (!membership) return 0
    return getRoleRank(membership.roleName)
  }, [user, activeOrgId])

  // Filter roles to only those at or below the current user's rank
  const availableRoles = useMemo(() => {
    if (!roles) return []
    return (roles as Role[]).filter((role) => getRoleRank(role.name) <= currentUserRank)
  }, [roles, currentUserRank])

  const roleOptions: SelectOption[] = useMemo(() => {
    return availableRoles.map((role) => ({
      label: role.displayName,
      value: role.name,
    }))
  }, [availableRoles])

  const selectedRoleOption: SelectOption[] = useMemo(() => {
    if (!form.role.value) return []
    return roleOptions.filter((o) => o.value === form.role.value)
  }, [form.role.value, roleOptions])

  const { mutate: inviteMember, isPending } = useMutation({
    mutationFn: (data: { orgId: string; email: string; role: string }) =>
      memberApi.csc.inviteMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberQueries.all() })
      toast({
        title: t('members.memberInvited'),
        description: t('members.invitationSent', { email: form.email.value }),
        type: 'success',
      })
      onClose()
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.email?.[0] ||
        error?.response?.data?.non_field_errors?.[0] ||
        t('members.inviteError')
      setApiError(message)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setApiError(null)
    form.validate()
    if (!form.isValid) return
    inviteMember({
      orgId,
      email: form.email.value!,
      role: form.role.value!,
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('members.inviteMember')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            label={form.email.label}
            placeholder={form.email.placeholder}
            value={form.email.value ?? ''}
            type="email"
            onChange={(e) => createFormFieldChangeHandler(form.email)(e.target.value)}
            onBlur={() => form.email.validate()}
            autoFocus
            data-testid="invite-email"
          />
          <ErrorsList errors={form.email.errors} />
        </div>

        <div>
          <SelectWrapper label={t('members.role')} data-testid="invite-role-wrapper">
            <CustomSelect
              options={roleOptions}
              values={selectedRoleOption}
              onChange={(values) => {
                createFormFieldChangeHandler(form.role)(values[0]?.value ?? '')
              }}
              placeholder={t('members.selectRole')}
              multi={false}
              clearable={false}
            />
          </SelectWrapper>
          <ErrorsList errors={form.role.errors} />
        </div>

        {apiError && (
          <p className="text-sm text-error" data-testid="invite-api-error">
            {apiError}
          </p>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" isLoading={isPending} data-testid="invite-submit">
            {t('members.sendInvite')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export const InviteMemberModal: React.FC<InviteMemberModalProps> = (props) => {
  return (
    <FormProvider<InviteMemberFormInputs>
      formClass={InviteMemberForm}
      key={props.isOpen ? 'open' : 'closed'}
    >
      <InviteMemberModalContent {...props} />
    </FormProvider>
  )
}
